import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import type { startseite } from "@progressive-development/politik-model";

import { blockStyles } from "../../../common/styles/block-styles.js";
import { zahl, prozent } from "../../../common/format/de-format.js";
import "../../parteien/components/app-partei-link.js";
import { parteiFarbe } from "../parteien.js";
import type { Ergebniszeile, WeitereZeile } from "../wahl-kurzfassung.js";

/**
 * Die Ergebnisliste — EINE Zeile für alle Wahl-Anzeigen des Portals.
 *
 * Farbe · Name · Balken · Anteil · Stimmen, dazu wahlweise der Klammerwert
 * der Gesamtstadt (Gebietsseite) und die Sammelzeile „weitere". Sie steht auf
 * der Startseite (drei Kräfte + weitere, mit Balken), auf Bezirks- und
 * Stadtteilseite (dazu die Stadtwerte) und auf der Detailseite (alle Kräfte).
 * Der Unterschied ist, was hereinkommt — nicht, was das Bauteil tut.
 *
 * DAS BAUTEIL SORTIERT NICHT, KÜRZT NICHT, RECHNET NICHT. Zeilen, Anteile
 * und Sammelzeile kommen fertig aus `wahl-kurzfassung.ts`; hier wird gezeigt,
 * was übergeben wird, in der Reihenfolge, in der es übergeben wird.
 *
 * DIE LÄNGE IST DER ANTEIL — sonst nichts (Fachkonzept 6.3). Ein Balken von
 * 37,7 % ist 37,7 % der Spur lang; vier Balken einer Kurzfassung füllen
 * zusammen die Spur, weil sie zusammen 100 % sind. Nicht „die stärkste Kraft
 * ist 100 %": Das machte aus einem Anteil einen Rang.
 *
 * Farbe identifiziert, sie wertet nicht: gleiche Höhe, gleiche Sättigung für
 * alle, nie ohne Beschriftung daneben. Ohne Verzeichniseintrag der neutrale
 * Datenton (`wahlen/parteien.ts`).
 *
 * Der Klammerwert steht als Zahl UND als dünne Marke im Balken: Man sieht,
 * ob die Säule den Stadtwert überragt, ohne eine zweite Spalte zu lesen —
 * und liest die Zahl, wenn man es genau wissen will. Keine Differenz, keine
 * Richtung: Der Raumvergleich fragt nach Lage, nicht nach Veränderung (6.5).
 */
@customElement("app-wahl-ergebnisliste")
@localized()
export class AppWahlErgebnisliste extends LitElement {
  /** Die Zeilen — fertig sortiert, mit Anteilen. */
  @property({ attribute: false }) zeilen: Ergebniszeile[] = [];

  /** Die Sammelzeile; fehlt, wo alle Kräfte genannt sind. */
  @property({ attribute: false }) weitere?: WeitereZeile;

  @property({ attribute: false }) parteien: startseite.ParteiEintrag[] = [];

  /** Gültige Stimmen — die Bezugsgröße, wird unter der Liste genannt. */
  @property({ type: Number }) bezug?: number;

  /**
   * Beschriftung des Klammerwerts, etwa „Köln". Steht an JEDEM Wert, damit
   * die Klammerspalte nicht als zweite Rangliste gelesen wird (Fachkonzept 4.3).
   * Ohne Beschriftung werden keine Klammerwerte gezeigt.
   */
  @property() klammerName = "";

  /**
   * Die Balken wachsen einmal aus der Nulllinie, wenn die Liste ins Bild
   * kommt — die einzige Bewegung der Startseite. Auf der Detailseite aus:
   * Dort liest man schon.
   */
  @property({ type: Boolean }) animiert = false;

  /**
   * Der Klammerwert zusätzlich als Strich in der Spur. **Standard: aus.**
   *
   * Beim Raumvergleich („Köln 25,0 %") sagt der Strich nichts, was die Klammer
   * nicht schon sagt — und wo der Stadtwert über dem lokalen liegt, steht er
   * frei neben dem Balken und wirkt wie ein Darstellungsfehler (Befund
   * 28.08.2026). Deshalb ausgeschaltet, aber nicht entfernt: Für einen
   * Vergleich mit derselben Größe an derselben Achse — die vorige Wahl
   * derselben Partei — trägt er sehr wohl, und dann steht er hier bereit.
   */
  @property({ type: Boolean }) mitMarke = false;

  @state() private _gewachsen = false;

  private _observer?: IntersectionObserver;

  static override styles = [
    blockStyles,
    css`
      :host {
        padding: 0;
        max-width: none;
      }

      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      li,
      .weitere {
        display: grid;
        grid-template-columns: minmax(6rem, 9rem) 1fr auto;
        align-items: center;
        gap: var(--portal-space-4);
        padding: var(--portal-space-3) 0;
        border-top: 1px solid var(--portal-linie);
      }
      li:last-child,
      .weitere {
        border-bottom: 1px solid var(--portal-linie);
      }
      .weitere {
        border-top: none;
        color: var(--portal-ink-2);
      }

      .name {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-sm);
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .weitere .name {
        font-weight: 400;
      }

      /* --- Balken: gleiche Höhe für alle, Länge = Anteil ------------------ */
      .spur {
        position: relative;
        height: 1.25rem;
      }
      .balken {
        height: 100%;
        border-radius: var(--portal-radius);
        transform-origin: left center;
        transition: width var(--portal-bar-duration) ease-out;
      }
      .weitere .balken {
        background: var(--portal-daten-25);
      }
      /*
       * Der Stadtwert als dünner Strich in der Spur — GESTRICHELT und in
       * zweiter Tinte. Voll ausgezogen und in Tinte las er sich wie ein
       * Darstellungsfehler im Balken statt wie ein Vergleichswert; die Zahl
       * dazu steht ohnehin in der Klammer daneben.
       *
       * Als Rahmen statt als Fläche, weil eine Fläche nicht gestrichelt sein
       * kann: 0 breit, der Strich ist die linke Kante.
       */
      .marke {
        position: absolute;
        top: -3px;
        bottom: -3px;
        width: 0;
        border-left: 1px dashed var(--portal-ink-2);
      }

      /* --- Werte: Anteil groß, Stimmen klein, Klammerwert daneben --------- */
      .werte {
        display: grid;
        grid-template-columns: auto auto;
        column-gap: var(--portal-space-3);
        align-items: baseline;
        justify-items: end;
        text-align: right;
      }
      .anteil {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-lg);
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .weitere .anteil {
        font-weight: 400;
      }
      .stimmen,
      .klammer {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      .klammer {
        grid-column: 2;
        grid-row: 1 / span 2;
        align-self: center;
        min-width: 6.5rem;
      }
      .stimmen {
        grid-column: 1;
      }
      .anteil {
        grid-column: 1;
      }

      .bezug {
        margin: var(--portal-space-3) 0 0;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
      }

      @media (max-width: 640px) {
        li,
        .weitere {
          grid-template-columns: 6.5rem 1fr;
          grid-template-areas:
            "name spur"
            "werte werte";
          row-gap: var(--portal-space-2);
        }
        .name {
          grid-area: name;
        }
        .spur {
          grid-area: spur;
        }
        .werte {
          grid-area: werte;
          display: flex;
          gap: var(--portal-space-3);
          align-items: baseline;
          text-align: left;
        }
        .klammer {
          min-width: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .balken {
          transition: none;
        }
      }
    `,
  ];

  override firstUpdated(): void {
    if (!this.animiert) {
      this._gewachsen = true;
      return;
    }
    const reduziert = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduziert || typeof IntersectionObserver === "undefined") {
      this._gewachsen = true;
      return;
    }
    this._observer = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) {
          this._gewachsen = true;
          this._observer?.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    this._observer.observe(this);
  }

  override disconnectedCallback(): void {
    this._observer?.disconnect();
    super.disconnectedCallback();
  }

  private _breite(anteil: number | undefined): number {
    if (!this._gewachsen || anteil === undefined) return 0;
    return Math.max(0, Math.min(100, anteil));
  }

  private _renderKlammer(wert: number | undefined) {
    if (!this.klammerName) return nothing;
    // Fehlend fehlt: ohne Stadtwert keine Klammer — kein Strich, keine 0.
    if (wert === undefined) return nothing;
    return html`<span class="klammer">(${this.klammerName} ${prozent(wert)})</span>`;
  }

  private _renderZeile(z: Ergebniszeile, i: number) {
    return html`
      <li>
        <span class="name" title=${z.name}
          ><app-partei-link slug=${z.partei ?? ""} text=${z.name}></app-partei-link
        ></span>
        <div class="spur">
          <div
            class="balken"
            style="width: ${this._breite(z.anteil)}%;
                   background: ${parteiFarbe(this.parteien, z.partei)};
                   transition-delay: ${this.animiert ? i * 60 : 0}ms"
          ></div>
          ${
            this.mitMarke && this.klammerName && z.klammerwert !== undefined
              ? html`<span
                  class="marke"
                  style="left: ${this._breite(z.klammerwert)}%"
                  title=${msg(str`${this.klammerName}: ${prozent(z.klammerwert)}`, {
                    id: "app.wahl.liste.markeTitel",
                  })}
                ></span>`
              : nothing
          }
        </div>
        <span class="werte">
          <span class="anteil">${z.anteil !== undefined ? prozent(z.anteil) : nothing}</span>
          <span class="stimmen">${zahl(z.stimmen)}</span>
          ${this._renderKlammer(z.klammerwert)}
        </span>
      </li>
    `;
  }

  private _renderWeitere() {
    const w = this.weitere;
    if (!w) return nothing;
    return html`
      <div class="weitere">
        <span class="name"
          >${
            w.anzahl === 1
              ? msg("1 weiterer Wahlvorschlag", { id: "app.wahl.liste.weitereEiner" })
              : msg(str`${w.anzahl} weitere Wahlvorschläge`, {
                  id: "app.wahl.liste.weitere",
                })
          }</span
        >
        <div class="spur">
          <div class="balken" style="width: ${this._breite(w.anteil)}%"></div>
        </div>
        <span class="werte">
          <span class="anteil">${w.anteil !== undefined ? prozent(w.anteil) : nothing}</span>
          <span class="stimmen">${zahl(w.stimmen)}</span>
          ${this.klammerName ? html`<span class="klammer"></span>` : nothing}
        </span>
      </div>
    `;
  }

  override render() {
    if (this.zeilen.length === 0) return nothing;
    return html`
      <ul>
        ${this.zeilen.map((z, i) => this._renderZeile(z, i))}
      </ul>
      ${this._renderWeitere()}
      ${
        this.bezug !== undefined
          ? html`<p class="bezug">
              ${msg(str`Anteil an den gültigen Stimmen — ${zahl(this.bezug)}`, {
                id: "app.wahl.liste.bezug",
              })}
            </p>`
          : nothing
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-wahl-ergebnisliste": AppWahlErgebnisliste;
  }
}
